import { customerHolderAge, formatCompactMoney } from '@/features/campaigns/customer-derived';
import type { Customer } from '@/features/intelligence-dashboard/customer.schema';

/**
 * Evaluates a single product eligibility rule against a customer.
 *
 * SA-1 fix: replaces the JS `test()` functions in the original bankiq-products.js
 * (which cannot be JSON-serialized) with a typed switch over the rule keys the
 * seed declares (18: the original 16, plus the two SCRUM-869 age keys).
 *
 * The switch must cover EVERY key in `product_rules`. An unhandled key hits the
 * fail-closed default, which means the criterion is silently IGNORED — that is the
 * SCRUM-869 defect, where three products' age gates were dropped and the UI still
 * reported "2/2 criteria met". `seed-rule-contract.test.ts` now fails the build if
 * the seed declares a key with no case here.
 *
 * Rule semantics: returns true when the customer DOES NOT already have the
 * product/attribute in question (i.e., they are eligible for cross-sell).
 *
 * SCRUM-843 — NUMERIC rules take their `threshold` from the product definition
 * rather than a constant hardcoded per rule KEY. The old shape was actively
 * misleading: `total_deposit_balance` is shared by the IRA, Business Choice Money
 * Market and ICS with labels of $5,000 / $25,000 / $250,000, yet all three were
 * judged at one hardcoded $25,000 — so ICS was gated an order of magnitude below
 * what its label promised, and VIP Platinum ($250,000) at $50,000. Passing the
 * threshold in makes the label the single source of truth.
 *
 * SCRUM-878 — the parameters now arrive over `/api/products`, so the SCRUM-877
 * interim fallback (which substituted the pre-SCRUM-843 constants when the transport
 * dropped them) is gone and strict fail-closed is restored. "Absent" once again means
 * "the product declared no such parameter", not "the DTO lost it" — a parameterised
 * rule with no parameter is a data defect and must not silently pass at some other
 * product's figure.
 */
export function evaluateRule(
  key: string,
  c: Customer,
  threshold?: number,
  expects?: string,
): boolean {
  /** Numeric comparison against the product-declared threshold; fail-closed. */
  const atLeast = (value: number): boolean => typeof threshold === 'number' && value >= threshold;

  switch (key) {
    case 'has_checking':
      return !c.holdings.includes('CHK');
    case 'has_savings':
      return !c.holdings.includes('SAV');
    case 'has_cd':
      return !c.holdings.includes('CD');
    case 'has_money_market':
      return !c.holdings.includes('MM');
    case 'has_hsa':
      return !c.holdings.includes('HSA');
    case 'has_ira':
      return !c.holdings.includes('IRA');
    case 'has_business_savings':
      return c.accountType === 'Business' && !c.holdings.includes('SAV');
    case 'has_ics':
      // ICS is a sweep product requiring a separate custodian agreement;
      // always returns false for the demo dataset → SA-7: filtered out.
      return false;
    case 'customer_type':
      return expects !== undefined && c.accountType === expects;
    case 'account_status':
      // All loaded customers are active — the dataset only contains active accounts.
      return true;
    case 'account_count':
      return atLeast(c.holdings.length);
    case 'account_tenure_months':
      return atLeast(c.ageMo);
    case 'total_relationship_balance':
    case 'total_deposit_balance':
    case 'total_dda_balance':
    case 'total_savings_balance':
      return atLeast(c.balance);
    // SCRUM-869 — age bands. The bound DIRECTION lives in the key because
    // ProductRule has no operator column: `age_at_least` is inclusive,
    // `age_below` is exclusive (mirroring the declared `age >= 50` / `age < 18`).
    //
    // Both fail closed when the holder age is absent. That is not a formality:
    // Business customers have no individual holder age, and it is how
    // "50+ Interest Checking" excludes them — that product declares no
    // customer_type rule, so the age gate is the only thing keeping businesses
    // out of a personal product.
    case 'age_at_least': {
      const age = customerHolderAge(c);
      return age !== null && atLeast(age);
    }
    case 'age_below': {
      const age = customerHolderAge(c);
      return age !== null && typeof threshold === 'number' && age < threshold;
    }
    default:
      reportUnknownRuleKey(key);
      return false;
  }
}

/**
 * SCRUM-869 — an unmapped rule key used to return `false` in total silence, so a
 * product could declare a criterion that was never evaluated and nothing said so.
 * That is precisely how the age criterion went missing for three products while
 * the UI reported a confident "2/2 criteria met".
 *
 * Still FAIL-CLOSED (an un-evaluable criterion must never count as satisfied),
 * but now audible. Logged once per key: this runs per customer per product, so
 * 2,000 customers would otherwise flood the console and bury the signal.
 */
const warnedRuleKeys = new Set<string>();

function reportUnknownRuleKey(key: string): void {
  if (warnedRuleKeys.has(key)) return;
  warnedRuleKeys.add(key);
  console.error(
    `[products] Rule key "${key}" has no evaluator case — the criterion is being ` +
      `IGNORED (fail-closed, so nothing matches on it). Add a case in ` +
      `rule-evaluator.ts (SCRUM-869).`,
  );
}

/** Test-only: clears the once-per-key log guard so each test can assert it. */
export function __resetRuleKeyWarnings(): void {
  warnedRuleKeys.clear();
}

/**
 * Returns the customer's actual value for the field a rule inspects, formatted
 * for display next to the rule label in the ICP-match popover (SCRUM-275). E.g.
 * a balance rule → "$84K"; the tenure rule → "18 mo". Returns `undefined` for
 * boolean holding-presence rules (the ✓/✗ already conveys them — no scalar to
 * show) and for rules with no meaningful value.
 *
 * The customer fields read here (`balance`, `ageMo`, `holdings`, `accountType`)
 * are REAL data from `/api/customers` (PostgreSQL via bankiq-api), so the
 * annotation reflects the actual customer in each insight row.
 *
 * Keys mirror `evaluateRule` above. The threshold drift this note used to warn
 * about (hardcoded per key, contradicting the label) was fixed in SCRUM-843 —
 * thresholds now come from the product definition, so a label and the value it is
 * judged against cannot disagree. The annotation shows the customer's actual value.
 */
export function annotateCriterion(key: string, c: Customer): string | undefined {
  switch (key) {
    case 'total_relationship_balance':
    case 'total_deposit_balance':
    case 'total_dda_balance':
    case 'total_savings_balance':
      return formatCompactMoney(c.balance);
    case 'account_tenure_months':
      return `${String(c.ageMo)} mo`;
    case 'account_count':
      return `${String(c.holdings.length)} acct${c.holdings.length === 1 ? '' : 's'}`;
    case 'customer_type':
      return c.accountType;
    // SCRUM-869 — show the age the gate was judged against, exactly as balance
    // and tenure already are. Business has no holder age, so no annotation.
    case 'age_at_least':
    case 'age_below': {
      const age = customerHolderAge(c);
      return age === null ? undefined : `${String(age)} yrs`;
    }
    // Boolean holding-presence rules + always-on/always-off rules carry no
    // meaningful scalar — the ✓/✗ alone is clearer than a noisy annotation.
    case 'has_checking':
    case 'has_savings':
    case 'has_cd':
    case 'has_money_market':
    case 'has_hsa':
    case 'has_ira':
    case 'has_business_savings':
    case 'has_ics':
    case 'account_status':
    default:
      return undefined;
  }
}
